import { createRootRouteWithContext, HeadContent, Outlet, Scripts, useHydrated } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { createServerFn } from "@tanstack/react-start";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { NavigationProgress } from "@/components/navigation-progress";
import { ThemeProvider } from "@/components/theme-provider";
import { resolveAtlasV2DesignEnabled } from "@/lib/atlas-design";
import { brandHex } from "@/lib/brand";
import { resolveDeployVersion } from "@/lib/deploy-target";
import { toPageHead } from "@/lib/page-metadata";
import { buildRootMetadata } from "@/lib/site-metadata";
import type { AtlasQueryContext } from "@/lib/query-client";
import GlobalError from "@/app/global-error";
import { StatusPage } from "@/components/status-page";
import { Button } from "@/components/ui/button";
import "../styles/fonts.css";
import "../app/globals.css";

const loadDocument = createServerFn({ method: "GET" }).handler(async () => ({
  design: await resolveAtlasV2DesignEnabled() ? "v2" : "v1",
  deploymentId: resolveDeployVersion(),
}));

export const Route = createRootRouteWithContext<AtlasQueryContext>()({
  loader: () => loadDocument(),
  head: () => toPageHead(buildRootMetadata(), { viewport: {
    width: "device-width", initialScale: 1, viewportFit: "cover",
    interactiveWidget: "resizes-content", colorScheme: "light dark",
  } }),
  shellComponent: RootDocument,
  component: RootLayout,
  errorComponent: GlobalError,
  notFoundComponent: () => (
    <StatusPage eyebrow="404" title="Page not found" description="This page is unavailable or has moved.">
      <Button asChild><a href="/">Return to Atlas</a></Button>
    </StatusPage>
  ),
});

function RootLayout() {
  return <><NavigationProgress /><Outlet /></>;
}

export function RootDocument({ children }: { children: ReactNode }) {
  const data = Route.useLoaderData();
  const hydrated = useHydrated();
  return (
    <html lang="en" data-scroll-behavior="smooth" data-atlas-design={data?.design ?? "v2"}
      data-dpl-id={data?.deploymentId} suppressHydrationWarning className="h-full antialiased">
      <head><meta charSet="utf-8" /><HeadContent /><meta name="theme-color" media="(prefers-color-scheme: light)" content={brandHex.ivory} /><meta name="theme-color" media="(prefers-color-scheme: dark)" content={brandHex.backgroundDark} /></head>
      {/* Translation must not replace server text before React claims it.
          Re-enable it after hydration so research and controls stay translatable. */}
      <body translate={hydrated ? undefined : "no"} className="min-h-full flex flex-col bg-background text-foreground">
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
          {children}
        </ThemeProvider>
        {process.env.NODE_ENV === "production" ? <SpeedInsights /> : null}
        <Scripts />
      </body>
    </html>
  );
}
